/**
 * STEEZY Brand Color Palette
 * Organized by color family with variations from lightest (1) to darkest (9)
 */

// Type for color variations
type ColorVariations = {
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
  6: string;
  7: string;
  8: string;
  9: string;
};

// Type for gray variations (1-8)
type GrayVariations = {
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
  6: string;
  7: string;
  8: string;
};

// Type for the entire color palette
export type ColorPalette = {
  mintColor: ColorVariations;
  waterGreen: ColorVariations;
  skyColor: ColorVariations;
  lightBlue: ColorVariations;
  mediumPurple: ColorVariations;
  hotPink: ColorVariations;
  redMagenta: ColorVariations;
  softOrange: ColorVariations;
  canaryYellow: ColorVariations;
  gray: GrayVariations;
};

// Primary brand colors
export const brandColors = {
  justWhite: "#FFFFFF",
  cementGray: "#EBEFEF", // Same as gray[1]
  notBlack: "#111111", // Same as gray[8]
  laBlue: "#0B79FB",
  cleanMint: "#81F1AE",
} as const;

// Color constants
export const colors: ColorPalette = {
  mintColor: {
    1: "#c8f9db",
    2: "#b6f7d0",
    3: "#a4f5c5",
    4: "#93f3b9",
    5: "#81f1ae",
    6: "#6fcf96",
    7: "#5dae7d",
    8: "#4b8c65",
    9: "#396a4d",
  },
  waterGreen: {
    1: "#8feee9",
    2: "#6be9e1",
    3: "#47e4da",
    4: "#24ded3",
    5: "#00d9cc",
    6: "#00bbaf",
    7: "#009c93",
    8: "#007e76",
    9: "#00605a",
  },
  skyColor: {
    1: "#a6dff7",
    2: "#8ad5f5",
    3: "#6ecaf2",
    4: "#51c0f0",
    5: "#35b6ed",
    6: "#2e9dcc",
    7: "#2683ab",
    8: "#1f6a89",
    9: "#175068",
  },
  lightBlue: {
    1: "#94c4fd",
    2: "#71b1fd",
    3: "#4f9efc",
    4: "#2d8cfc",
    5: "#0b79fb",
    6: "#0968d8",
    7: "#0857b5",
    8: "#064692",
    9: "#05356f",
  },
  mediumPurple: {
    1: "#bcaff2",
    2: "#a796ee",
    3: "#927dea",
    4: "#7d63e6",
    5: "#684ae2",
    6: "#5940c2",
    7: "#4b35a3",
    8: "#3c2b83",
    9: "#2e2164",
  },
  hotPink: {
    1: "#ee9fd7",
    2: "#e880cb",
    3: "#e361be",
    4: "#dd43b2",
    5: "#d824a5",
    6: "#ba1f8e",
    7: "#9c1a77",
    8: "#7d1560",
    9: "#5f1049",
  },
  redMagenta: {
    1: "#eb9e9e",
    2: "#e57f7f",
    3: "#df6060",
    4: "#d84141",
    5: "#d22222",
    6: "#b51d1d",
    7: "#971818",
    8: "#7a1414",
    9: "#5c0f0f",
  },
  softOrange: {
    1: "#f9bfa5",
    2: "#f7aa89",
    3: "#f6966c",
    4: "#f48150",
    5: "#f26d33",
    6: "#d05e2c",
    7: "#ae4f25",
    8: "#8c3f1e",
    9: "#6b3016",
  },
  canaryYellow: {
    1: "#ffe08f",
    2: "#ffd66b",
    3: "#ffcc47",
    4: "#ffc224",
    5: "#ffb800",
    6: "#db9e00",
    7: "#b88500",
    8: "#946b00",
    9: "#705100",
  },
  gray: {
    1: "#EBEFEF", // Lightest - Cement Gray
    2: "#C8CBCB",
    3: "#A5A8A8",
    4: "#848787",
    5: "#656666",
    6: "#474848",
    7: "#2B2C2C",
    8: "#111111", // Darkest - Not Black
  },
};

// Common color combinations and semantic names
export const semanticColors = {
  primary: brandColors.laBlue, // LA Blue as primary brand color
  secondary: colors.redMagenta[5], // Red Magenta as secondary color
  success: brandColors.cleanMint, // Clean Mint for success states
  warning: colors.canaryYellow[5], // Canary Yellow for warnings
  error: colors.redMagenta[5], // Red Magenta for errors
  info: colors.lightBlue[5], // Light Blue for information
  background: brandColors.justWhite, // Just White as background
  surface: brandColors.cementGray, // Cement Gray as surface color
  text: brandColors.notBlack, // Not Black as text color
  textSecondary: colors.gray[4], // Medium gray for secondary text
  disabled: colors.gray[3], // Light-medium gray for disabled states
  divider: colors.gray[2], // Light gray for dividers
} as const;

// Helper function to get color with type safety
export function getColor(
  family: keyof ColorPalette,
  variation: number
): string {
  if (family === "gray" && variation > 8) {
    throw new Error("Gray variations only go up to 8");
  }
  return colors[family][variation as keyof (ColorVariations | GrayVariations)];
}
